// Conexión a Firebase de Comandia multi-local (un solo proyecto para todos los
// locales) + a qué local corresponde la URL que se está viendo.
// ES EL MISMO ARCHIVO en panel/ y sitio/ (lo controla pruebas/slugs.test.ts).
//
// La configuración la inyecta layout.tsx en window.COMANDIA desde las
// variables NEXT_PUBLIC_* del build. Con NEXT_PUBLIC_FIREBASE_EMULADOR=1 (lo
// pone .env.development, o sea `npm run dev`) todo va a los emuladores locales
// del proyecto "demo-comandia": desde desarrollo no se pueden tocar datos reales.

const COMANDIA: ConfigComandia = window.COMANDIA || {};
const USANDO_EMULADOR = COMANDIA.emulador === true;

const firebaseConfig: Record<string, string> = USANDO_EMULADOR
  ? { apiKey: "demo-clave", authDomain: "demo-comandia.firebaseapp.com", projectId: "demo-comandia" }
  : COMANDIA.firebase || {};

const FIREBASE_CONFIGURADO = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

let firebaseApp: AppFirebase | null = null;
let db: Firestore | null = null;
let auth: Auth | null = null;
// Las Cloud Functions todavía no validan a qué local pertenece quien las llama
// (DISEÑO.md §10): hasta que lo hagan, quedan apagadas en la versión multi-local.
const functions: Funciones | null = null;

if (FIREBASE_CONFIGURADO && typeof firebase !== "undefined") {
  firebaseApp = firebase.initializeApp(firebaseConfig);
  db = firebase.firestore();
  if (typeof firebase.auth === "function") auth = firebase.auth();
  if (USANDO_EMULADOR) {
    db.useEmulator("127.0.0.1", 8080);
    // El segundo parámetro (silenciar el aviso) existe en el SDK pero no en sus tipos.
    if (auth) (auth.useEmulator as (url: string, opciones?: { disableWarnings: boolean }) => void)("http://127.0.0.1:9099", { disableWarnings: true });
  }

  // Un local no siempre tiene buen wifi, y la caja no puede dejar de andar por
  // eso. Con esto Firestore guarda lo que ya bajó y encola lo que se escribe:
  // el panel sigue funcionando sin señal y sincroniza solo cuando vuelve.
  // synchronizeTabs permite tener el panel abierto en más de una pestaña (caja
  // y cocina, por ejemplo). Si el navegador no lo soporta, se sigue sin esto.
  db.enablePersistence({ synchronizeTabs: true }).catch(() => {
    /* modo incógnito o navegador viejo: anda igual, sin caché */
  });
}

// ---------- Local según la URL ----------

// Mismas reglas que slugValido() en firestore.rules. Toda página nueva de nivel
// superior en cualquiera de las dos apps se suma acá Y en las reglas.
const SLUGS_RESERVADOS = [
  "404", "admin", "administrador", "api", "app", "assets", "ayuda",
  "blog", "carta", "comandia", "contacto", "css", "cuenta", "cuentas",
  "demo", "entrar", "equipo", "estado", "facturacion", "faq", "icon",
  "img", "index", "ingresar", "js", "legal", "local", "locales", "login",
  "logout", "mail", "membresia", "membresias", "mesa", "mesas",
  "nosotros", "opengraph-image", "pagos", "panel", "pedido", "pedidos",
  "planes", "precios", "privacidad", "prueba", "registrar", "registro",
  "robots", "root", "salir", "seguridad", "sistema", "sitemap",
  "soporte", "static", "status", "terminos", "test", "www"
];

function slugEsValido(slug: unknown): slug is string {
  return (
    typeof slug === "string" &&
    /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(slug) &&
    !slug.includes("--") &&
    !SLUGS_RESERVADOS.includes(slug)
  );
}

// "La Esquina de Doña Ñata" → "la-esquina-de-dona-nata"
function slugDesdeNombre(nombre: string): string {
  return (nombre || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .slice(0, 40)
    .replace(/-+$/, "");
}

// En producción el slug es el primer tramo de la URL (comandia.com.ar/la-esquina):
// Netlify sirve la página "cascarón" /local sin cambiar la dirección. Entrando
// directo a /local se acepta ?local=la-esquina.
const SLUG_LOCAL: string | null = (function () {
  const primerTramo = decodeURIComponent(window.location.pathname.split("/")[1] || "").toLowerCase();
  const candidato =
    primerTramo === "local" ? new URLSearchParams(window.location.search).get("local") || "" : primerTramo;
  return slugEsValido(candidato) ? candidato : null;
})();

function refDocLocal(): Documento {
  return db.collection("locales").doc(SLUG_LOCAL);
}

function refLocal(coleccion: string): Coleccion {
  return refDocLocal().collection(coleccion);
}

// Un documento de Firestore no viene tipado: data() devuelve DatosDocumento, y
// al expandirlo se pierde el modelo del local. Estos dos lo devuelven, y de paso
// dejan escrito en una sola linea de que coleccion sale cada lista.
function docConId<T>(doc: { id: string; data(): DatosDocumento | undefined }): ConId<T> {
  return { id: doc.id, ...(doc.data() || {}) } as ConId<T>;
}

function docsConId<T>(snapshot: { docs: { id: string; data(): DatosDocumento }[] }): ConId<T>[] {
  return snapshot.docs.map((doc) => docConId<T>(doc));
}

// Links públicos de un local (QR, invitaciones). `ruta` va pegada al slug:
// "/mesa?m=abc" o "?modo=carta".
function urlSitioLocal(slug: string, ruta?: string): string {
  return `${(COMANDIA.urlSitio || "").replace(/\/$/, "")}/${slug}${ruta || ""}`;
}

function urlPanelLocal(slug: string): string {
  return `${(COMANDIA.urlPanel || "").replace(/\/$/, "")}/${slug}`;
}

// ---------- Fotos de los platos ----------
// La foto no va dentro del documento del plato: la carta escucha el menú
// entero y bajaría todas las fotos en cada visita y en cada cambio de precio.
// Vive en locales/{slug}/fotos/{platoId} = { imagenUrl, version } y el plato
// guarda solo `fotoVersion`. El navegador guarda cada foto por versión (Cache
// Storage), así una carta ya visitada no la vuelve a bajar, y se bajan recién
// cuando están por aparecer en pantalla. Los platos con `imagenUrl` adentro
// (formato anterior) se siguen mostrando igual. DISEÑO.md §11.

/** Lo que FotosPlatos necesita saber de un plato para mostrar su foto. */
interface PlatoConFoto {
  id: string;
  fotoVersion?: string;
  imagenUrl?: string | null;
}

const FotosPlatos = (function () {
  const NOMBRE_CACHE = "comandia-fotos-v1";
  const listas = new Map<string, string>(); // clave → data URL ya bajada en esta página
  const pendientes = new Map<string, Promise<string | null>>(); // clave → promesa en curso
  const observadores = new WeakMap<Element, IntersectionObserver>(); // contenedor → IntersectionObserver
  const estadisticas = { navegador: 0, servidor: 0 };
  const clave = (platoId: string, version: string) =>
    `/fotos-platos/${SLUG_LOCAL}/${encodeURIComponent(platoId)}/${encodeURIComponent(version)}`;
  const escapar = (texto: unknown) => String(texto).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

  async function abrirCache(): Promise<Cache | null> {
    try {
      return typeof caches === "undefined" ? null : await caches.open(NOMBRE_CACHE);
    } catch (error) {
      return null; // modo privado, o sitio sin https
    }
  }

  function tiene(plato: PlatoConFoto): boolean {
    return Boolean(plato && (plato.imagenUrl || plato.fotoVersion));
  }

  function cargar(plato: PlatoConFoto): Promise<string | null> {
    if (plato.imagenUrl) return Promise.resolve(plato.imagenUrl);
    if (!plato.fotoVersion || !db || !SLUG_LOCAL) return Promise.resolve(null);
    const k = clave(plato.id, plato.fotoVersion);
    if (listas.has(k)) return Promise.resolve(listas.get(k));
    if (pendientes.has(k)) return pendientes.get(k);

    const promesa = (async (): Promise<string | null> => {
      const cache = await abrirCache();
      const guardada = cache ? await cache.match(k).catch((): Response | null => null) : null;
      if (guardada) {
        estadisticas.navegador++;
        return guardada.text();
      }
      const doc = await refLocal("fotos").doc(plato.id).get();
      const foto = doc.exists ? doc.data() : null;
      if (!foto || !foto.imagenUrl) return null;
      estadisticas.servidor++;
      if (cache && foto.version === plato.fotoVersion) {
        await cache.put(k, new Response(foto.imagenUrl)).catch(() => {});
        // Las versiones anteriores de esa misma foto ya no sirven.
        const prefijo = clave(plato.id, "");
        for (const vieja of await cache.keys()) {
          const ruta = new URL(vieja.url).pathname;
          if (ruta.startsWith(prefijo) && ruta !== k) cache.delete(vieja);
        }
      }
      return foto.imagenUrl as string;
    })()
      .catch((): string | null => null)
      .then((src: string | null) => {
        pendientes.delete(k);
        if (src) listas.set(k, src);
        return src;
      });
    pendientes.set(k, promesa);
    return promesa;
  }

  // Atributos para el <img> de un plato: el src si la foto ya está a mano (así
  // redibujar la carta no parpadea); si no, lo que activar() necesita.
  function atributos(plato: PlatoConFoto): string {
    const lista = plato.imagenUrl || listas.get(clave(plato.id, plato.fotoVersion));
    return lista
      ? `src="${escapar(lista)}"`
      : `data-foto-plato="${escapar(plato.id)}" data-foto-version="${escapar(plato.fotoVersion)}"`;
  }

  // Baja las fotos pendientes de `contenedor` a medida que se acercan a la
  // pantalla. Si una no se puede bajar, se quita su caja ([data-foto-caja]).
  function activar(contenedor: Element): void {
    if (observadores.has(contenedor)) observadores.get(contenedor).disconnect();
    const mostrar = (img: HTMLImageElement) =>
      cargar({ id: img.dataset.fotoPlato, fotoVersion: img.dataset.fotoVersion }).then((src) => {
        if (src) img.src = src;
        else (img.closest("[data-foto-caja]") || img).remove();
      });
    const imagenes = contenedor.querySelectorAll<HTMLImageElement>("img[data-foto-plato]");
    if (typeof IntersectionObserver === "undefined") {
      imagenes.forEach(mostrar);
      return;
    }
    const observador = new IntersectionObserver(
      (entradas) =>
        entradas.forEach((entrada) => {
          if (!entrada.isIntersecting) return;
          observador.unobserve(entrada.target);
          mostrar(entrada.target as HTMLImageElement);
        }),
      { rootMargin: "600px 0px" }
    );
    imagenes.forEach((img) => observador.observe(img));
    observadores.set(contenedor, observador);
  }

  // Versión = huella del contenido: la misma foto siempre da la misma versión
  // (migracion/migrar.ts la calcula igual).
  async function version(dataUrl: string): Promise<string> {
    try {
      const huella = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(dataUrl));
      return [...new Uint8Array(huella)]
        .slice(0, 8)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    } catch (error) {
      return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    }
  }

  return { tiene, cargar, atributos, activar, version, estadisticas };
})();
